import { useState, useEffect, useCallback } from 'react';
import { getStaff, updateStaffRole, deleteStaff } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { UserCog, Search, Trash2, Shield, User } from 'lucide-react';
import { toast } from 'sonner';

export default function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);

  const loadStaff = useCallback(async () => {
    try {
      const response = await getStaff();
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleRoleChange = async (staffId, newRole) => {
    try {
      await updateStaffRole(staffId, newRole);
      toast.success('Role updated successfully');
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const confirmDelete = (staffMember) => {
    setStaffToDelete(staffMember);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!staffToDelete) return;
    
    try {
      await deleteStaff(staffToDelete.id);
      toast.success('Staff member deleted');
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff');
    } finally {
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
    }
  };

  const filteredStaff = staff.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="staff-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Staff Management</h1>
        <p className="text-stone-500 mt-1">Manage team members and their access levels</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-stone-100 rounded-md">
              <UserCog className="w-5 h-5 text-stone-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">{staff.length}</p>
              <p className="text-sm text-stone-500">Total Staff</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sage-50 rounded-md">
              <Shield className="w-5 h-5 text-sage-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">
                {staff.filter(s => s.role === 'admin').length}
              </p>
              <p className="text-sm text-stone-500">Administrators</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-md">
              <User className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900 font-['Manrope']">
                {staff.filter(s => s.role === 'staff').length}
              </p>
              <p className="text-sm text-stone-500">Staff Members</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-staff-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="p-12 text-center">
            <UserCog className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No staff members found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50 hover:bg-stone-50">
                <TableHead className="font-semibold text-stone-700">Name</TableHead>
                <TableHead className="font-semibold text-stone-700">Email</TableHead>
                <TableHead className="font-semibold text-stone-700">Role</TableHead>
                <TableHead className="font-semibold text-stone-700 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStaff.map((staffMember, index) => (
                <TableRow 
                  key={staffMember.id}
                  className="table-row-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`staff-row-${staffMember.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center text-stone-700 font-medium">
                        {staffMember.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{staffMember.name}</p>
                        {staffMember.id === user?.id && (
                          <span className="text-xs text-sage-700">(You)</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-stone-600">{staffMember.email}</TableCell>
                  <TableCell>
                    <Select 
                      value={staffMember.role}
                      onValueChange={(value) => handleRoleChange(staffMember.id, value)}
                      disabled={staffMember.id === user?.id}
                    >
                      <SelectTrigger 
                        className="w-32 border-stone-300"
                        data-testid={`role-select-${staffMember.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-sage-700" />
                            Admin
                          </div>
                        </SelectItem>
                        <SelectItem value="staff">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-blue-700" />
                            Staff
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    {staffMember.id !== user?.id && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => confirmDelete(staffMember)}
                        className="h-8 w-8 p-0 text-stone-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-staff-${staffMember.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Manrope']">Delete Staff Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{staffToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-staff"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
